import { toast } from 'services/toast';
import { copySurfaceNodesByReferenceMutation } from 'graphql/surface-node/mutations/copy-surface-nodes-by-reference';
import { useTranslation } from 'i18n';
import useMutation from 'hooks/use-mutation';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import { surfaceNodesQuery } from 'graphql/surface-node/queries/surface-nodes';
import Response from 'services/graphql/response';
import ResponseError from 'services/graphql/response-error';
import { Position } from 'types';

interface CopySurfaceNodesByReferenceInput {
  id: string;
  surface?: string;
  position?: Position;
}

export default function useCopySurfaceNodesByReference(
  surfaceId: string,
): (
  input: CopySurfaceNodesByReferenceInput[],
) => Promise<any | Response<any> | ResponseError<Error>> {
  const { t } = useTranslation();
  const { findNodeById } = useFindNodeInCache(surfaceId);
  const [copySurfaceNode] = useMutation(copySurfaceNodesByReferenceMutation);

  return function (input: CopySurfaceNodesByReferenceInput[]) {
    return copySurfaceNode({
      variables: { input },
      refetchQueries: [
        {
          query: surfaceNodesQuery,
          variables: { input: { filter: { surface: surfaceId } } },
        },
      ],
      awaitRefetchQueries: true,
    }).then((response) => {
      if (response.isFailure) {
        return toast.error(response.getErrorMessage());
      }

      toast.success(
        t(`Successfully copied nodes {{names}}`, {
          replace: {
            names:
              input
                .map(({ id }) => findNodeById(id))
                .map((node) => node?.name)
                .join(', ') || '',
          },
        }),
      );

      return response;
    });
  };
}
